/**
 * GA4 Event Helpers —  Implementation Guide
 *
 * Centralized module for pushing GA4-standard events to window.dataLayer
 * via the existing trackEvent() utility.
 *
 * Each exported function corresponds to one GA4 event from the guide.
 * Components import the relevant function and call it at the appropriate moment.
 */

import { trackEvent, hasViewedPromotion, markPromotionViewed } from './index';
import { LineItem } from 'types/entity/cart';
import { Money, Product, Variant } from 'types/entity/product';
import {
  GA4_VIEW_ITEM_LIST,
  GA4_SELECT_ITEM,
  GA4_VIEW_ITEM,
  GA4_ADD_TO_CART,
  GA4_ADD_TO_WISHLIST,
  GA4_REMOVE_FROM_CART,
  GA4_VIEW_CART,
  GA4_BEGIN_CHECKOUT,
  GA4_ADD_PAYMENT_INFO,
  GA4_PURCHASE,
  GA4_VIEW_PROMOTION,
  GA4_SELECT_PROMOTION,
  GA4_MENU_NAVIGATION,
  GA4_FORM_EVENT,
  GA4_CTA_CLICK,
  GA4_NEWSLETTER_SIGN_UP,
  GA4_ITEM_REVIEW_SUBMIT,
  GA4_ITEM_GALLERY_VIEW,
  GA4_ITEM_CUSTOMIZE,
  GA4_PROJECT_START,
  GA4_PROJECT_RESUME,
  GA4_PROJECT_DELETE,
  GA4_PROJECT_SAVE,
  GA4_PROJECT_ACTION,
  GA4_ERROR_EVENT,
} from 'helpers/constants/events';
import { DELIVERY_METHOD_LABELS } from 'helpers/constants/cart';
import { AttributeMapper } from 'components/commercetools-ui/organisms/product/product-details/helpers/attributeMapper';
import { logAuditEvent } from 'helpers/otel/documentLoad';
import { getAttributeLabel, getAttributeValue } from 'helpers/utils/attributes';


// ---------------------------------------------------------------------------
// GA4 Item interface — matches the items[] spec from the guide
// ---------------------------------------------------------------------------

export interface GA4Item {
  item_name?: string;
  item_id?: string;
  coupon?: string;
  discount?: number;
  item_list_id?: string;
  item_list_name?: string;
  item_variant?: string;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  price?: number;
  index?: number;
  quantity?: number;
  item_size?: string;
  item_color?: string;
  item_material?: string | number | boolean;
  [key: string]: any;
  item_stand?:any;
  item_trim?:any;
  item_img_url?:any;
  item_border?:any;
  item_mat?:any;
  item_shape?:any;
  item_pack_qty?:any;
  item_envelope_printing?:any;
  item_cover_type?:any;
  item_calendar_start_date?:any;
  item_back_design?:any;
  item_url?:string;
}

// ---------------------------------------------------------------------------
// Helpers — convert CT domain objects to GA4 items[]
// ---------------------------------------------------------------------------

export function getPdpUrl(data: Record<string, any>): any {
  return (
    data?._url ?? ''
  );
}

export function getImageUrl(data: Record<string, any>): any {
  return (
    data?.variants[0]?.images?.[0] ?? ''
  );
}
export function getItemVariant(data: Record<string, any>): any {
  return (
    data?.attributes?.display_name_universal ?? ''
  );
}

export function getSizeFromAttrs(attrs: Record<string, any>): any {
  return (
    getAttributeLabel(attrs, 'size_pp') ||
    getAttributeLabel(attrs, 'size_dd') ||
    getAttributeLabel(attrs, 'size_wa') ||
    getAttributeLabel(attrs, 'size_cards') ||
    getAttributeLabel(attrs, 'size_nb') ||
    getAttributeLabel(attrs, 'size_ornaments') ||
    getAttributeLabel(attrs, 'size_calendars') ||
    getAttributeLabel(attrs, 'size_cm') ||
    getAttributeLabel(attrs, 'size_textiles') ||
    getAttributeLabel(attrs, 'book_size_pb') ||
    getAttributeLabel(attrs, 'size') ||
    getAttributeLabel(attrs, 'item_size')
  );
}

/** Convert a centAmount + fractionDigits to a decimal number (e.g. 12.99) */
const centsToDecimal = (centAmount?: number, fractionDigits = 2): number | undefined => {
  if (centAmount === undefined || centAmount === null) return undefined;
  return Number((centAmount / Math.pow(10, fractionDigits)).toFixed(fractionDigits));
};

/** Strip special characters per guide convention (®, ©, ™, —, diacritics) */
/** Strip special characters per guide convention (®, ©, ™, —, diacritics) */
export const stripSpecialChars = (value?: any): string | undefined => {
  if (value === undefined || value === null) return undefined;

  let stringValue: string;
  if (Array.isArray(value)) {
    stringValue = value.filter((v) => v !== undefined && v !== null).join(', ');
  } else {
    stringValue = String(value);
  }

  if (!stringValue.trim()) return undefined;

  return stringValue
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[®©™—]/g, '')
    .trim();
};

/** Capitalize only the first letter (e.g. "visa" -> "Visa") */
const capitalizeFirstLetter = (value?: string): string | undefined => {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};

/** Get GA4 Order Type and Shipping Info from distribution channel key */
export const getGA4OrderContext = (distributionChannelKey?: string) => {
  if (!distributionChannelKey) return { orderType: undefined, shippingInfo: undefined };

  if (distributionChannelKey.startsWith('p2r-')) {
    let shippingInfo = 'Other';
    if (distributionChannelKey.includes('walmart')) shippingInfo = 'Walmart';
    if (distributionChannelKey.includes('walgreens')) shippingInfo = 'Walgreens';
    if (distributionChannelKey.includes('cvs')) shippingInfo = 'CVS';
    return { orderType: 'P2R', shippingInfo };
  }

  if (distributionChannelKey.startsWith('m2h-')) {
    return { orderType: 'M2H', shippingInfo: 'Online' };
  }

  return { orderType: undefined, shippingInfo: undefined };
};

function applyCategoryMappingForLineItem(
  item: GA4Item,
  attrs: any,
  categoryInfo?: { categoryName?: string },
) {
  const fallbackParentCategory = categoryInfo?.categoryName;

  const categoryCandidates: CategoryValueOrArray[] = [
    attrs.product_category_universal,
    attrs.item_category,
    attrs.item_category2,
    attrs.item_category3,
    attrs.item_category4,
    attrs.item_category5,
    getAttributeValue(attrs, 'event_category_universal') as CategoryValueOrArray,
    fallbackParentCategory,
  ];

  const taxonomyMapped = mapToTaxonomyCategories(categoryCandidates);

  item.item_category = taxonomyMapped.item_category
    ? stripSpecialChars(taxonomyMapped.item_category)
    : (normalizeCategoryValue(attrs.product_category_universal) ?? normalizeCategoryValue(attrs.item_category));

  item.item_category2 = taxonomyMapped.item_category2
    ? stripSpecialChars(taxonomyMapped.item_category2)
    : (normalizeCategoryValue(attrs.item_category2) ??
      normalizeCategoryValue(getAttributeValue(attrs, 'event_category_universal') as CategoryValueOrArray) ??
      normalizeCategoryValue(attrs.item_category3) ??
      normalizeCategoryValue(attrs.item_category4));

  item.item_category3 = taxonomyMapped.item_category3
    ? stripSpecialChars(taxonomyMapped.item_category3)
    : (normalizeCategoryValue(attrs.item_category3) ?? normalizeCategoryValue(attrs.item_category5));

  const cat4 = extractCategoryValue(attrs.item_category4);
  if (cat4) item.item_category4 = stripSpecialChars(cat4);

  const cat5 = extractCategoryValue(attrs.item_category5);
  if (cat5) item.item_category5 = stripSpecialChars(cat5);
}

function applyVariantAttributesForLineItem(enableGA4:boolean,item: GA4Item, attrs: any, variant?: Variant, attributesToDisplay?: any) {
  const primaryAttr = attributesToDisplay?.primaryAttribute;

  const size = getSizeFromAttrs(attrs) ?? (primaryAttr ? getAttributeLabel(variant?.attributes, primaryAttr) : undefined);

  if (size) item.item_size = size;

  const color = getAttributeLabel(variant?.attributes, 'color_dd') ?? attrs.color ?? attrs.item_color;
  if (color) item.item_color = stripSpecialChars(color);

  const material =
    getAttributeLabel(attrs, 'material_dd') ||
    getAttributeLabel(attrs, 'material_cm') ||
    getAttributeLabel(attrs, 'material_wa') ||
    getAttributeLabel(attrs, 'material_textiles') ||
    getAttributeLabel(attrs, 'material_ornaments') ||
    getAttributeLabel(attrs, 'material') ||
    getAttributeLabel(attrs, 'item_material');

  if (material) item.item_material = stripSpecialChars(material);

  // trim
  const trim = getAttributeLabel(variant?.attributes, 'trim_cards');
  if(enableGA4 && trim) item.item_trim = trim;

  //Images
  const itemImage =variant?.images?.[0];
  if(enableGA4 && itemImage) item.item_img_url = itemImage;

  // Border
  const LineItemBorder = variant?.attributes?.borderless_pp;
  if(enableGA4 && LineItemBorder) item.item_border = LineItemBorder;

  // Has Stand
  const hasStand = variant?.attributes?.has_stand_wa;
  if(enableGA4 && hasStand) item.item_stand = hasStand;

  // Mat 
  const hasMat = getAttributeLabel(variant?.attributes, 'mat_included_wa');
  if(enableGA4 && hasMat) item.item_mat = hasMat;

  // Shape 
  const itemShape = getAttributeLabel(variant?.attributes, 'product_shape_ornaments');
  if(enableGA4 && itemShape) item.item_shape = itemShape;

  // Pack Qty
  const packQty = getAttributeLabel(variant?.attributes, 'pack_size_cards');
  if(enableGA4 && packQty) item.item_pack_qty = packQty;

  // item_envelope_printing
  const envelopPrint = getAttributeLabel(variant?.attributes, 'envelope_cards') || variant?.attributes?.envelope_included_cards;
  if(enableGA4 && envelopPrint) item.item_envelope_printing = envelopPrint;

  // Cover Type
  const coverType = getAttributeLabel(variant?.attributes, 'cover_type_pb')
  if(enableGA4 && coverType) item.item_cover_type = coverType;

  // Cover Type
  const calanderDate = getAttributeLabel(variant?.attributes, 'starting_month')
  if(enableGA4 && calanderDate) item.item_calendar_start_date = calanderDate;

  // // Cover Type
  // const backDesign = variant?.attributes?.designer_universal || variant?.attributes?.agency_design_id_universal || variant?.attributes?.design_name_universal
  // if( enableGA4 && backDesign) item.item_back_design = backDesign;

  const variantDetails = variant?.attributes?.design_name_universal;
  if (enableGA4 && variantDetails) item.item_variant = variantDetails;

}

/**
 *  Shared method used by applyDiscountForLineItem and applyDiscountForLineItemTotal
 **/
function computeLineItemDiscountAmounts(lineItem: LineItem, divisor: number) {
  const allCoupons =
    lineItem.allDiscounts?.couponDiscounts
      ?.map((coupon) => coupon.couponCode)
      .filter((code): code is string => code !== undefined) || [];

  const coupon =
    Array.from(new Set(allCoupons))
      .sort((a, b) => a.localeCompare(b))
      .join(' | ') || undefined;

  const saleDiscount = (lineItem.allDiscounts?.salePriceDiscount?.centAmount ?? 0) / divisor;
  const promoDiscount =
    (lineItem.allDiscounts?.couponDiscounts?.reduce(
      (acc, coupon) => acc + (coupon.discountAmount?.centAmount ?? 0),
      0,
    ) ?? 0) / divisor;

  return { coupon, saleDiscount, promoDiscount };
}

/**
 *  Shared method used by applyDiscountForLineItem and applyDiscountForLineItemTotal
 **/
function applyDiscountAmounts(
  item: GA4Item,
  price: Money | undefined,
  amounts: { coupon?: string; saleDiscount: number; promoDiscount: number },
) {
  const discount = amounts.saleDiscount + amounts.promoDiscount;

  item.discount = centsToDecimal(discount, price?.fractionDigits) ?? 0;
  if (amounts.saleDiscount > 0) {
    item.sale_discount = centsToDecimal(amounts.saleDiscount, price?.fractionDigits) ?? 0;
  }
  if (amounts.promoDiscount > 0) {
    item.promo_discount = centsToDecimal(amounts.promoDiscount, price?.fractionDigits) ?? 0;
  }
  item.coupon = amounts.coupon;
}

function applyDiscountForLineItem(item: GA4Item, lineItem: LineItem, price?: Money) {
  const divisor = lineItem.count ?? 1;
  applyDiscountAmounts(item, price, computeLineItemDiscountAmounts(lineItem, divisor));
}

/** Same as applyDiscountForLineItem but keeps discount amounts as line totals, not divided by count */
function applyDiscountForLineItemTotal(item: GA4Item, lineItem: LineItem, price?: Money) {
  applyDiscountAmounts(item, price, computeLineItemDiscountAmounts(lineItem, 1));
}

type LineItemGA4Opts = {
  index?: number;
  itemListName?: string;
  itemListId?: string;
  categoryInfo?: { categoryName?: string };
};

/**
 * Shared builder behind mapLineItemToGA4Item / mapLineItemToGA4ItemTotal; callers supply the
 * effective price (unit vs. line total) and the matching discount applier.
 */
function buildGA4ItemFromLineItem(
  enableGA4: boolean,
  lineItem: LineItem,
  effectivePriceCentAmount: number,
  applyDiscount: (item: GA4Item, lineItem: LineItem, price?: Money) => void,
  opts?: LineItemGA4Opts,
): GA4Item {
  const variant = lineItem.variant;
  const attrs = variant?.attributes ?? {};
  const attributesToDisplay = variant && AttributeMapper(variant, '');
  const price = lineItem.price ?? variant?.price;

  const item: GA4Item = {};

  // Name
  const name = attrs.display_name_universal ?? lineItem.name;
  if (name) item.item_name = stripSpecialChars(name);

  // ID
  item.item_id = variant?.sku ?? lineItem.productId ?? (lineItem as any).id;

  // Price
  const priceNum = centsToDecimal(effectivePriceCentAmount, price?.fractionDigits);
  if (priceNum !== undefined) item.price = priceNum;

  // Quantity
  if (lineItem.count !== undefined) item.quantity = lineItem.count;

  // Index
  if (opts?.index !== undefined) item.index = opts.index + 1;

  // List metadata
  if (opts?.itemListName) item.item_list_name = opts.itemListName;
  if (opts?.itemListId) item.item_list_id = opts.itemListId;

  // Brand
  const brand = attrs.brand_universal ?? attrs.brand;
  if (brand) item.item_brand = stripSpecialChars(brand);

  applyCategoryMappingForLineItem(item, attrs, opts?.categoryInfo);
  applyVariantAttributesForLineItem(enableGA4, item, attrs, variant, attributesToDisplay);
  applyDiscount(item, lineItem, price);

  return Object.fromEntries(Object.entries(item).filter(([, v]) => v !== undefined)) as GA4Item;
}

/**
 * Map a Commerce Tools LineItem to a GA4 items[] entry.
 */
export const mapLineItemToGA4Item = (enableGA4: boolean, lineItem: LineItem, opts?: LineItemGA4Opts): GA4Item => {
  const quantity = lineItem.count ?? 1;
  const totalPrice = lineItem.originalTotalPrice?.centAmount ?? 0;
  const totalDiscount = lineItem.allDiscounts?.totalDiscount?.centAmount ?? 0;
  const effectiveUnitPrice = (totalPrice - totalDiscount) / quantity;

  return buildGA4ItemFromLineItem(enableGA4, lineItem, effectiveUnitPrice, applyDiscountForLineItem, opts);
};

/**
 * Same as mapLineItemToGA4Item but keeps price and discount as line totals instead of
 * dividing by quantity/count.
 */
export const mapLineItemToGA4ItemTotal = (enableGA4: boolean, lineItem: LineItem, opts?: LineItemGA4Opts): GA4Item => {
  const totalPrice = lineItem.originalTotalPrice?.centAmount ?? 0;
  const totalDiscount = lineItem.allDiscounts?.totalDiscount?.centAmount ?? 0;
  const effectiveTotalPrice = totalPrice - totalDiscount;

  return buildGA4ItemFromLineItem(enableGA4, lineItem, effectiveTotalPrice, applyDiscountForLineItemTotal, opts);
};

/**
 * Map a Product (PDP / product-list) to a GA4 items[] entry.
 */
type TaxonomyResult = {
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
};

const TOP_LEVEL = new Set(['prints', 'photo books', 'cards', 'calendars', 'wall decor', 'photo gifts']);

const SUB_BY_TOP: Record<string, Set<string>> = {
  prints: new Set(['standard prints', 'square prints', 'poster prints', 'specialty prints']),
  'photo books': new Set([]),
  cards: new Set([
    'invitations',
    'birthday',
    'baby',
    'graduation',
    'thank you',
    'occasions + holidays',
    'specialty cards',
  ]),
  calendars: new Set([]),
  'wall decor': new Set(['canvas prints', 'mounted prints', 'acrylic prints', 'metal prints', 'framed prints']),
  'photo gifts': new Set(['drinkware', 'puzzles', 'blankets', 'desktop', 'magnets', 'ornaments', 'notebooks']),
};

const SUBSUB_BY_SUB: Record<string, Set<string>> = {
  invitations: new Set([
    'wedding invites',
    'graduation party invites',
    'birthday invites',
    'party invites',
    'baby shower invites',
  ]),
  birthday: new Set(['birthday greetings', 'birthday invites', 'birthday thank yous']),
  baby: new Set(['baby shower invites', 'baby announcement', 'baby thank yous']),
  graduation: new Set(['graduation party invites', 'graduation announcements', 'graduation thank yous']),
  'thank you': new Set([
    'thank yous',
    'birthday thank yous',
    'kids thank yous',
    'wedding thank yous',
    'graduation thank yous',
  ]),
  'occasions + holidays': new Set([
    'religious & spiritual',
    'spanish language',
    'new years',
    'cultural celebrations',
    "father's day",
    "mother's day",
    "valentine's day",
    'anniversary',
  ]),
  'specialty cards': new Set(['create your own', 'foil cards', 'magnet cards', 'postcards']),
};

const TOP_BY_SUB: Record<string, string> = Object.entries(SUB_BY_TOP).reduce(
  (acc, [top, subs]) => {
    subs.forEach((s) => (acc[s] = top));
    return acc;
  },
  {} as Record<string, string>,
);

const SUB_BY_SUBSUB: Record<string, string> = Object.entries(SUBSUB_BY_SUB).reduce(
  (acc, [sub, subsubs]) => {
    subsubs.forEach((subsub) => {
      acc[subsub] = sub;
    });
    return acc;
  },
  {} as Record<string, string>,
);

const ALL_TAXONOMY_VALUES = (() => {
  const all = new Set<string>();
  TOP_LEVEL.forEach((v) => all.add(v));
  Object.values(SUB_BY_TOP).forEach((set) => set.forEach((v) => all.add(v)));
  Object.values(SUBSUB_BY_SUB).forEach((set) => set.forEach((v) => all.add(v)));
  return all;
})();

const ALIASES: Record<string, string> = {
  'acrylic print': 'acrylic prints',
  'canvas print': 'canvas prints',
  'mounted print': 'mounted prints',
  'metal print': 'metal prints',
  'framed print': 'framed prints',
  'standard print': 'standard prints',
  'square print': 'square prints',
  'poster print': 'poster prints',
  'specialty print': 'specialty prints',
  'photo book': 'photo books',
  'new year': 'new years',
  'valentines day': "valentine's day",
  'wedding program': 'wedding invites',
  'wedding programs': 'wedding invites',
  wall_decor: 'wall decor',
  'wall-decor': 'wall decor',
  photo_gifts: 'photo gifts',
  'photo-gifts': 'photo gifts',
};

function norm(raw?: string): string | undefined {
  if (!raw) return undefined;
  const v = String(raw).toLowerCase().trim().replace(/[_-]/g, ' ').replace(/\s+/g, ' ');
  return ALIASES[v] ?? v;
}

function extractCategoryValue(raw?: CategoryValue | CategoryValue[]): string | undefined {
  if (raw === undefined || raw === null) return undefined;

  const values: CategoryValue[] = Array.isArray(raw) ? raw : [raw];
  const firstDefined = values.find((item) => item !== undefined && item !== null);
  if (firstDefined === undefined) return undefined;

  if (typeof firstDefined === 'object') {
    return String(
      (firstDefined as any).name ?? (firstDefined as any).label ?? (firstDefined as any).value ?? firstDefined,
    );
  }

  return String(firstDefined);
}

type CategoryValue = string | number | boolean | null | undefined | { name?: string; label?: string; value?: string };
type CategoryValueOrArray = CategoryValue | CategoryValue[] | undefined;

function normalizeCategoryValue(raw?: CategoryValue | CategoryValue[]): string | undefined {
  if (raw === undefined || raw === null) return undefined;

  const values: CategoryValue[] = Array.isArray(raw) ? raw : [raw];
  const firstDefined = values.find((item) => item !== undefined && item !== null);
  if (firstDefined === undefined) return undefined;

  let normalizedRaw: string;
  if (typeof firstDefined === 'object') {
    normalizedRaw = String(
      (firstDefined as any).name ?? (firstDefined as any).label ?? (firstDefined as any).value ?? firstDefined,
    );
  } else {
    normalizedRaw = String(firstDefined);
  }

  const normalized = norm(normalizedRaw);
  return stripSpecialChars(normalized ?? normalizedRaw);
}

function pickFirst<T>(arr: T[], pred: (t: T) => boolean): T | undefined {
  for (const x of arr) if (pred(x)) return x;
  return undefined;
}

export function mapToTaxonomyCategories(inputs: Array<CategoryValueOrArray>): TaxonomyResult {
  const normalized = inputs
    .flatMap((value) => {
      if (Array.isArray(value)) {
        return value
          .filter((v) => v !== undefined && v !== null)
          .map((v) =>
            typeof v === 'object' ? String((v as any).name ?? (v as any).label ?? (v as any).value ?? v) : String(v),
          );
      }
      if (value === undefined || value === null) return [];
      if (typeof value === 'object') {
        return [String((value as any).name ?? (value as any).label ?? (value as any).value ?? value)];
      }
      return [String(value)];
    })
    .map(norm)
    .filter((x): x is string => Boolean(x));
  const recognized = normalized.filter((x) => ALL_TAXONOMY_VALUES.has(x));

  let top = pickFirst(recognized, (x) => TOP_LEVEL.has(x));

  if (!top) {
    const anySub = pickFirst(recognized, (x) => Boolean(TOP_BY_SUB[x]));
    if (anySub) top = TOP_BY_SUB[anySub];
  }

  let sub: string | undefined;
  let subsub: string | undefined;

  if (top) {
    sub = pickFirst(recognized, (x) => SUB_BY_TOP[top as string]?.has(x) ?? false);
    subsub = sub ? pickFirst(recognized, (x) => SUBSUB_BY_SUB[sub as string]?.has(x) ?? false) : undefined;
  }

  if (!top) {
    sub = pickFirst(recognized, (x) => Boolean(TOP_BY_SUB[x]));
    if (sub) {
      top = TOP_BY_SUB[sub];
      subsub = pickFirst(recognized, (x) => SUBSUB_BY_SUB[sub as string]?.has(x) ?? false);
    }
  }

  if (!sub) {
    subsub = pickFirst(recognized, (x) => Boolean(SUB_BY_SUBSUB[x]));
    if (subsub) {
      sub = SUB_BY_SUBSUB[subsub];
      if (!top) top = TOP_BY_SUB[sub];
    }
  }

  return { item_category: top, item_category2: sub, item_category3: subsub };
}

function applyCategoryMapping(item: GA4Item, attrs: any, categories: any[]) {
  const categoryNames = categories.map((c) => c?.name).filter(Boolean);
  const categoryKeys = categories.map((c) => c?.categoryKey).filter(Boolean);

  const candidates: CategoryValueOrArray[] = [
    attrs.product_category_universal,
    attrs.item_category,
    attrs.item_category2,
    attrs.item_category3,
    attrs.item_category4,
    attrs.item_category5,
    getAttributeValue(attrs, 'event_category_universal') as CategoryValueOrArray,
    ...categoryNames,
    ...categoryKeys,
  ];

  const taxonomyMapped = mapToTaxonomyCategories(candidates);

  item.item_category = taxonomyMapped.item_category
    ? stripSpecialChars(taxonomyMapped.item_category)
    : (normalizeCategoryValue(attrs.product_category_universal) ?? normalizeCategoryValue(attrs.item_category));

  item.item_category2 = taxonomyMapped.item_category2
    ? stripSpecialChars(taxonomyMapped.item_category2)
    : (normalizeCategoryValue(attrs.item_category2) ??
      normalizeCategoryValue(getAttributeValue(attrs, 'event_category_universal') as CategoryValueOrArray) ??
      normalizeCategoryValue(attrs.item_category3) ??
      normalizeCategoryValue(attrs.item_category4));

  item.item_category3 = taxonomyMapped.item_category3
    ? stripSpecialChars(taxonomyMapped.item_category3)
    : (normalizeCategoryValue(attrs.item_category3) ?? normalizeCategoryValue(attrs.item_category5));

  const cat4 = extractCategoryValue(attrs.item_category4);
  if (cat4) item.item_category4 = stripSpecialChars(cat4);

  const cat5 = extractCategoryValue(attrs.item_category5);
  if (cat5) item.item_category5 = stripSpecialChars(cat5);
}

function applyVariantAttributes(enableGA4:any,item: GA4Item, variant: Variant | undefined, attrs: any, attributesToDisplay: any) {
  const primaryAttr = attributesToDisplay?.primaryAttribute;

  const size = getSizeFromAttrs(attrs) ?? (primaryAttr ? getAttributeLabel(variant?.attributes, primaryAttr) : undefined);

  if (size) item.item_size = size;

  const color = getAttributeLabel(variant?.attributes, 'color_dd') ?? attrs.color ?? attrs.item_color;
  if (color) item.item_color = stripSpecialChars(color);

 // trim
  const trim = getAttributeLabel(attrs, 'trim_cards');
  if(enableGA4 && trim) item.item_trim = trim;

  //Images
  const itemImage =variant?.images?.[0];
  if(enableGA4 && itemImage) item.item_img_url = itemImage;

  // Border
  const LineItemBorder = variant?.attributes?.borderless_pp;
  if(enableGA4 && LineItemBorder) item.item_border = LineItemBorder;

  // Has Stand
  const hasStand = variant?.attributes?.has_stand_wa;
  if(enableGA4 && hasStand) item.item_stand = hasStand;

  // Mat 
  const hasMat = getAttributeLabel(variant?.attributes, 'mat_included_wa');
  if(enableGA4 && hasMat) item.item_mat = hasMat;

  // Shape 
  const itemShape = getAttributeLabel(variant?.attributes, 'product_shape_ornaments');
  if(enableGA4 && itemShape) item.item_shape = itemShape;

  // Pack Qty
  const packQty = getAttributeLabel(variant?.attributes, 'pack_size_cards');
  if(enableGA4 && packQty) item.item_pack_qty = packQty;

  // item_envelope_printing
  const envelopPrint = getAttributeLabel(variant?.attributes, 'envelope_cards') || attrs?.envelope_included_cards;
  if(enableGA4 && envelopPrint) item.item_envelope_printing = envelopPrint;

  // Cover Type
  const coverType = getAttributeLabel(variant?.attributes, 'cover_type_pb')
  if(enableGA4 && coverType) item.item_cover_type = coverType;

  // Cover Type
  const calanderDate = getAttributeLabel(variant?.attributes, 'starting_month')
  if(enableGA4 && calanderDate) item.item_calendar_start_date = calanderDate;

  // // Cover Type
  // const backDesign = attrs?.designer_universal || attrs?.agency_design_id_universal || attrs?.design_name_universal
  // if(enableGA4 && backDesign) item.item_back_design = backDesign;

  // Variant Details
  const variantDetails = variant?.attributes?.design_name_universal;
  if (enableGA4 && variantDetails) item.item_variant = variantDetails;

}

function applyMaterial(item: GA4Item, attrs: any) {
  const material =
    getAttributeLabel(attrs, 'material_dd') ||
    getAttributeLabel(attrs, 'material_cm') ||
    getAttributeLabel(attrs, 'material_wa') ||
    getAttributeLabel(attrs, 'material_textiles') ||
    getAttributeLabel(attrs, 'material_ornaments') ||
    getAttributeLabel(attrs, 'material') ||
    getAttributeLabel(attrs, 'item_material');

  if (material) item.item_material = stripSpecialChars(material);
}

function applyDiscount(item: GA4Item, variant?: Variant, price?: Money) {
  if (variant?.discountedPrice?.value?.centAmount !== undefined && price?.centAmount !== undefined) {
    const discountAmt = price.centAmount - variant.discountedPrice.value.centAmount;
    if (discountAmt > 0) {
      item.discount = centsToDecimal(discountAmt, price.fractionDigits);
    }
  }
}

export const mapProductToGA4Item = (
  enableGA4:any,
  product: Product,
  opts?: {
    index?: number;
    itemListName?: string;
    itemListId?: string;
    variant?: Variant;
    coupon?: string;
    quantity?: number;
  },
): GA4Item => {
  const variant = opts?.variant ?? product.variants?.[0];
  const attrs = { ...product.attributes, ...variant?.attributes } as any;
  const price = variant?.price;
  const categories = product.categories ?? [];
  const subCategoryKey = product?.categories?.[0]?.categoryKey;
  const attributesToDisplay = AttributeMapper(variant, subCategoryKey ?? '');
  const item: GA4Item = {};

  const name = attrs.display_name_universal ?? product.name;
  if (name) item.item_name = stripSpecialChars(name);

  item.item_id = variant?.sku ?? product.productId ?? (product as any).id;

  if (opts?.coupon) item.coupon = opts.coupon;

  const priceNum = centsToDecimal(price?.centAmount, price?.fractionDigits);
  if (priceNum !== undefined) item.price = priceNum;

  if (opts?.index !== undefined) item.index = opts.index + 1;
  if (opts?.itemListName) item.item_list_name = opts.itemListName;
  if (opts?.itemListId) item.item_list_id = opts.itemListId;
  if (opts?.quantity !== undefined) item.quantity = opts.quantity;

  const brand = attrs.brand_universal ?? attrs.brand;
  if (brand) item.item_brand = stripSpecialChars(brand);

  const itemUrl = product?._url;
  if(itemUrl) item.item_url = itemUrl;

  applyCategoryMapping(item, attrs, categories);
  applyVariantAttributes(enableGA4,item, variant, attrs, attributesToDisplay);
  applyMaterial(item, attrs);
  applyDiscount(item, variant, price);

  return Object.fromEntries(Object.entries(item).filter(([, v]) => v !== undefined)) as GA4Item;
};

// ---------------------------------------------------------------------------
// 2. Enhanced Ecommerce Events
// ---------------------------------------------------------------------------

/** 2.2 Product List View */
export const trackViewItemList = (itemListName: string, items: GA4Item[]) => {
  trackEvent(GA4_VIEW_ITEM_LIST, {
    ecommerce: {
      item_list_name: itemListName,
      items,
    },
  });
};

/** 2.3 Product List Click */
export const trackSelectItem = (itemListName: string, items: GA4Item[]) => {
  trackEvent(GA4_SELECT_ITEM, {
    ecommerce: {
      item_list_name: itemListName,
      items,
    },
  });
};

/** 2.4 Product Detail Page */
export const trackViewItem = (currency: string, items: GA4Item[]) => {
  trackEvent(GA4_VIEW_ITEM, {
    ecommerce: {
      currency,
      items,
    },
  });
};

/** 2.5 Add to Cart */
export const trackAddToCart = (
  enableGA4: boolean,
  currency: string,
  value: number,
  cartId: string,
  cartUrl: string,
  items: GA4Item[],
  addToCartLocation?: string
) => {
  const payload: any = {
    ecommerce: {
      currency,
      value,
      items,
    },
  };

  if (addToCartLocation) {
    payload.add_to_cart_location = addToCartLocation;
  }

  if (enableGA4) {
    payload.cart_id = cartId;
    payload.cart_url = cartUrl;
  }

  trackEvent(GA4_ADD_TO_CART, payload);
};


/** 2.6 Add to Wishlist */
export const trackAddToWishlist = (currency: string, items: GA4Item[]) => {
  trackEvent(GA4_ADD_TO_WISHLIST, {
    ecommerce: {
      currency,
      items,
    },
  });
};


/** 2.7 Remove from Cart */
export const trackRemoveFromCart = (currency: string, value: number, cartId:string, cartUrl:string,  items: GA4Item[], enableGA4:boolean) => {
  
  const payload: any = {
    ecommerce: {
      currency,
      value,
      items,
    },
  };

  if (enableGA4) {
    payload.cart_id = cartId;
    payload.cart_url = cartUrl;
  }
  trackEvent(GA4_REMOVE_FROM_CART, payload);
};

/** 2.8 View Cart */
export const trackViewCart = (params: {
  enableGA4: boolean;
  currency: string;
  value: number;
  cartId: string;
  cartUrl: string;
  items: GA4Item[];
  orderType?: string;
  shippingInfo?: string;
}) => {
  const payload: any = {
    ecommerce: {
      currency: params.currency,
      value: params.value,
      items: params.items,
      order_type: params.orderType,
      Shipping_info: params.shippingInfo,
    },
  };

  if (params.enableGA4) {
    payload.cart_id = params.cartId;
    payload.cart_url = params.cartUrl;
  }

  trackEvent(GA4_VIEW_CART, payload);
};

/** 2.9 Begin Checkout */
export const trackBeginCheckout = (params: {
  enableGA4:boolean;
  currency: string;
  value: number;
  cartId:string,
  cartUrl:string,
  items: GA4Item[];
  orderType?: string;
  shippingInfo?: string;
}) => {

  const payload: any = {
     ecommerce: {
      currency: params.currency,
      value: params.value,
      items: params.items,
      order_type: params.orderType,
      Shipping_info: params.shippingInfo,
    },
  };

  if (params.enableGA4) {
    payload.cart_id = params.cartId;
    payload.cart_url = params.cartUrl;
  }
  trackEvent(GA4_BEGIN_CHECKOUT, payload);

};

const normalizeShippingTier = (shippingTier?: string): string | undefined => {
  if (!shippingTier) return undefined;
  return DELIVERY_METHOD_LABELS[shippingTier] ?? shippingTier;
};

/** 2.10 Add Shipping Info */
export const trackAddShippingInfo = (params: any) => {
  const {
    enableGA4,
    currency,
    value,
    cartId,
    cartUrl,
    items,
    shippingTier,
    coupon,
    orderType,
    shippingInfo,
  } = params;

  const payload: any = {
    ecommerce: {
      currency,
      value,
      items,
      ...(orderType && { order_type: orderType }),
      ...(shippingInfo && { shipping_info: shippingInfo }),
      ...(shippingTier && { shipping_tier: normalizeShippingTier(shippingTier) }),
      ...(coupon && { coupon }),
    },
  };

  if (enableGA4) {
    payload.cart_id = cartId;
    payload.cart_url = cartUrl;
  }

  trackEvent('add_shipping_info', payload);
};

/** 2.11 Add Payment Info */
export const trackAddPaymentInfo = (params: {
  enableGA4:boolean;
  currency: string;
  value: number;
  cartId:string,
  cartUrl:string,
  items: GA4Item[];
  paymentType?: string;
  coupon?: string;
  orderType?: string;
  shippingInfo?: string;
}) => {

  const ecommerce: Record<string, any> = {
    currency: params.currency,
    value: params.value,
    items: params.items,
    order_type: params.orderType,
    Shipping_info: params.shippingInfo,
  };
  const payload: any = {
     ecommerce:ecommerce,
  };

  if (params.enableGA4) {
    payload.cart_id = params.cartId;
    payload.cart_url = params.cartUrl;
  }
  if (params.paymentType) ecommerce.payment_type = capitalizeFirstLetter(params.paymentType);
  if (params.coupon) ecommerce.coupon = params.coupon;

  trackEvent(GA4_ADD_PAYMENT_INFO, payload);
};

/** 2.12 Purchase */
export const trackPurchase = (params: {
  discount:any,
  enableGA4:boolean;
  transactionId: string;
  orderId: string;
  affiliation?: string;
  currency: string;
  tax?: number;
  shipping?: number | string;
  value: any;
  cartId:string,
  cartUrl:string,
  coupon?: string;
  items: GA4Item[];
  orderType?: string;
  shippingInfo?: string;
  paymentType?: any,
  savings?: {
    saleSavings?: number;
    promoSavings?: number;
    shippingSavings?: number;
    totalSavings?: number;
  }
}) => {
  const ecommerce: Record<string, any> = {
    transaction_id: params.transactionId,
    order_id: params.orderId,
    currency: params.currency,
    value: params.value,
    items: params.items,
  };

   const payload: any = {
     ecommerce:ecommerce,
  };

   if (params.enableGA4) {
    payload.cart_id = params.cartId;
    payload.cart_url = params.cartUrl;
  }

  if (params.affiliation) ecommerce.affiliation = params.affiliation;
  if (params.tax !== undefined) ecommerce.tax = params.tax;
  if (params.shipping !== undefined) ecommerce.shipping = params.shipping;
  if (params.coupon) ecommerce.coupon = params.coupon;

  ecommerce.sale_savings = params.savings?.saleSavings ?? 0;
  ecommerce.promo_savings = params.savings?.promoSavings ?? 0;
  ecommerce.shipping_savings = params.savings?.shippingSavings ?? 0;
  ecommerce.total_savings = params.savings?.totalSavings ?? 0;

  ecommerce.order_type = params.orderType;
  ecommerce.shipping_info = params.shippingInfo;

  ecommerce.payment_type = capitalizeFirstLetter(params.paymentType);

  trackEvent(GA4_PURCHASE, payload);
  logAuditEvent('ORDER_COMPLETED', {
  orderId: params.orderId,
  transactionId: params.transactionId,
});
};

/** 2.13 View Promotion */
export const trackViewPromotion = (params: {
  creativeName?: string;
  creativeSlot?: string;
  promotionId?: string;
  promotionName?: string;
  items?: GA4Item[];
}) => {
  // Dedupe: view_promotion should fire at most once per promotion_id per page view.
  if (params.promotionId && hasViewedPromotion(params.promotionId)) return;

  const ecommerce: Record<string, any> = {};
  if (params.creativeName) ecommerce.creative_name = params.creativeName;
  if (params.creativeSlot) ecommerce.creative_slot = params.creativeSlot;
  if (params.promotionId) ecommerce.promotion_id = params.promotionId;
  if (params.promotionName) ecommerce.promotion_name = params.promotionName;
  if (params.items?.length) ecommerce.items = params.items;
  trackEvent(GA4_VIEW_PROMOTION, { ecommerce });

  if (params.promotionId) markPromotionViewed(params.promotionId);
};

/** 2.14 Select Promotion */
export const trackSelectPromotion = (params: {
  creativeName?: string;
  creativeSlot?: string;
  promotionId?: string;
  promotionName?: string;
  items?: GA4Item[];
}) => {
  const ecommerce: Record<string, any> = {};
  if (params.creativeName) ecommerce.creative_name = params.creativeName;
  if (params.creativeSlot) ecommerce.creative_slot = params.creativeSlot;
  if (params.promotionId) ecommerce.promotion_id = params.promotionId;
  if (params.promotionName) ecommerce.promotion_name = params.promotionName;
  if (params.items?.length) ecommerce.items = params.items;
  trackEvent(GA4_SELECT_PROMOTION, { ecommerce });
};

// ---------------------------------------------------------------------------
// 3. Standard / Non-Ecommerce Events
// ---------------------------------------------------------------------------

/** 3.1 Menu Navigation */
export const trackMenuNavigation = (menuType: string, menuText: string, linkUrl: string) => {
  trackEvent(GA4_MENU_NAVIGATION, {
    menu_type: menuType,
    menu_text: menuText,
    link_url: linkUrl,
  });
};

/** 3.2 Form Event (start / step / complete) */
export const trackFormEvent = (formId: string, formName: string, formStep: string) => {
  trackEvent(GA4_FORM_EVENT, {
    form_id: formId,
    form_name: formName,
    form_step: formStep,
  });
};

/** 3.3 CTA Click */
export const trackCtaClick = (ctaLocation: string, linkText: string, linkUrl?: string) => {
  trackEvent(GA4_CTA_CLICK, {
    cta_location: ctaLocation,
    link_text: linkText,
    link_url: linkUrl,
  });
};

/** 3.4 Newsletter Sign-Up */
export const trackNewsletterSignUp = (method: string, formId: string) => {
  trackEvent(GA4_NEWSLETTER_SIGN_UP, {
    method:method,
    form_id: formId,
  });
  logAuditEvent('NEWSLETTER_SIGNUP');
};

/** 3.5 Item Review Submit */
export const trackItemReviewSubmit = (reviewScore: string) => {
  trackEvent(GA4_ITEM_REVIEW_SUBMIT, {
    review_score: reviewScore,
  });
};

/** 3.6 Item Gallery View */
export const trackItemGalleryView = (params: {
  currency?: string;
  productName?: string;
  productId?: string;
  productCategory?: string;
  productCategory2?: string;
  productCategory3?: string;
  productPrice?: number;
  productSize?: string;
  productColor?: string;
  galleryImageName?: string;
  itemUrl?:string;
  itemImageUrl?:string;
  itemVariant?:string;
  material?:string;
  discountData?:any ;
  couponData?:string;
  quantityData?:any,
  hasBorder?:boolean,
  hasStand?:any,
  hasMat?:any,
  itemShape?:any,
  packQty?:any,
  envelopPrint?:any,
  coverType?:any,
  calanderDate?:any,
  backDesign?:any
}) => {
  const taxonomy = mapToTaxonomyCategories([params.productCategory, params.productCategory2, params.productCategory3]);

  trackEvent(GA4_ITEM_GALLERY_VIEW, {
    gallery_image_name: params.galleryImageName,
    ecommerce: {
      currency: params.currency,
      items: {
        product_name: params.productName,
        product_id: params.productId,
        product_category: taxonomy.item_category,
        product_category2: taxonomy.item_category2,
        product_category3: taxonomy.item_category3,
        product_price: params.productPrice,
        product_size: params.productSize,
        product_color: params.productColor,
        gallery_image_name: params.galleryImageName,
        ...(params.itemUrl && { item_url: params.itemUrl }),
        ...(params.itemImageUrl && { item_img_url: params.itemImageUrl }),
        ...(params.material && { item_material: params.material }),
        ...(params.discountData && { discount: params.discountData }),
        ...(params.quantityData && { quantity: params.quantityData }),
        ...(params.couponData && { coupon: params.couponData }),
        ...(params.hasBorder && { item_border: params.hasBorder }),
        ...(params.hasStand && { item_stand: params.hasStand }),
        ...(params.hasMat && { item_mat: params.hasMat }),
        ...(params.itemShape && { item_shape: params.itemShape }),
        ...(params.packQty && { item_pack_qty: params.packQty }),
        ...(params.coverType && { item_cover_type: params.coverType }),
        ...(params.envelopPrint && { item_envelope_printing: params.envelopPrint }),
        ...(params.calanderDate && { item_calendar_start_date: params.calanderDate }),
      },
    },
  });
};

/** 3.7 Item Customize */
export const trackItemCustomize = (params: {
  currency?: string;
  productName?: string;
  productId?: string;
  productCategory?: string;
  productCategory2?: string;
  productCategory3?: string;
  productPrice?: number;
  productSize?: string;
  productColor?: string;
  itemUrl?:string;
  itemImageUrl?:string;
  itemVariant?:string;
  material?:string;
  discountData?:any ;
  couponData?:string;
  quantityData?:any
  hasBorder?:boolean,
  hasStand?:any,
  hasMat?:any,
  itemShape?:any,
  packQty?:any,
  envelopPrint?:any,
  coverType?:any,
  calanderDate?:any,
  backDesign?:any,
  hasTrim?:any
}) => {
  const taxonomy = mapToTaxonomyCategories([params.productCategory, params.productCategory2, params.productCategory3]);

  trackEvent(GA4_ITEM_CUSTOMIZE, {
   ecommerce:{ 
    items: [
      { 
        currency: params.currency,
        item_name: params.productName,
        item_id: params.productId,
        item_category: taxonomy.item_category,
        item_category2: taxonomy.item_category2,
        item_category3: taxonomy.item_category3,
        price: params.productPrice,
        item_size: params.productSize,
        item_color: params.productColor,
        item_variant:params?.itemVariant,
        ...(params.itemUrl && { item_url: params.itemUrl }),
        ...(params.itemImageUrl && { item_img_url: params.itemImageUrl }),
        ...(params.material && { item_material: params.material }),
        ...(params.discountData && { discount: params.discountData }),
        ...(params.quantityData && { quantity: params.quantityData }),
        ...(params.couponData && { coupon: params.couponData }),
        ...(params.hasBorder && { item_border: params.hasBorder }),
        ...(params.hasStand && { item_stand: params.hasStand }),
        ...(params.hasMat && { item_mat: params.hasMat }),
        ...(params.itemShape && { item_shape: params.itemShape }),
        ...(params.packQty && { item_pack_qty: params.packQty }),
        ...(params.coverType && { item_cover_type: params.coverType }),
        ...(params.envelopPrint && { item_envelope_printing: params.envelopPrint }),
        ...(params.calanderDate && { item_calendar_start_date: params.calanderDate }),
         ...(params.hasTrim && { item_trim: params.hasTrim }),
      }
    ]
  }
  });
};

// ---------------------------------------------------------------------------
// Project events — shared payload shape
// ---------------------------------------------------------------------------

interface ProjectParams {
  projectId?: string;
  projectProductIdList?: string;
  projectProductNameList?: string;
  projectProductCategoryList?: string;
  projectProductCategory2List?: string;
  projectProductCategory3List?: string;
  projectExpiryDate?: string;
  eventName?:string;
  projectBuilder?: string;
}

const buildProjectPayload = (params: ProjectParams) => ({
  project_id: params.projectId,
  project_item_id_list: params.projectProductIdList,
  project_item_name_list: params.projectProductNameList,
  project_item_category_list: params.projectProductCategoryList,
  project_item_category2_list: params.projectProductCategory2List,
  project_item_category3_list: params.projectProductCategory3List,
  project_expiry_date: params.projectExpiryDate,
  project_builder: params.projectBuilder,
  ...(params.eventName && { event: params.eventName }),
});

/** 3.8 Project Start (Mediaclip) */
export const trackProjectStart = (params: ProjectParams) => {
  trackEvent(GA4_PROJECT_START, buildProjectPayload(params));
};

/** 3.9 Project Resume (Mediaclip) */
export const trackProjectResume = (params: ProjectParams) => {
  trackEvent(GA4_PROJECT_RESUME, buildProjectPayload(params));
};

/** 3.10 Project Delete (Mediaclip) */
export const trackProjectDelete = (params: ProjectParams) => {
  trackEvent(GA4_PROJECT_DELETE, buildProjectPayload(params));
  logAuditEvent('PROJECT_DELETED', {
   ...(params.projectId ? { projectId: params.projectId }
: {}),
});
};

/** 3.11 Project Save (Mediaclip) */
export const trackProjectSave = (params: ProjectParams) => {
  trackEvent(GA4_PROJECT_SAVE, buildProjectPayload(params));
};

/** 3.12 Project Action (Prints Builder & Mediaclip) */
export const trackProjectAction = (
  params: ProjectParams & {
    projectActionType?: string;
    projectActionLabel?: string;
  },
) => {
  trackEvent(GA4_PROJECT_ACTION, {
    ...buildProjectPayload(params),
    project_action_type: params.projectActionType,
    project_action_label: params.projectActionLabel,
  });
};

/** 3.13 Error Event */
export const trackErrorEvent = (errorMessage: string, errorLabel?: string) => {
  trackEvent(GA4_ERROR_EVENT, {
    error_message: errorMessage,
    error_label: errorLabel,
  });
};
